import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'

const RatingComponentSchema = z.object({
  points: z.number(),
  max_points: z.number(),
  rating: z.number(),
  weight: z.string(),
  weighted_rating: z.number(),
})

const BreakdownSchema = z.object({
  total_points: z.number(),
  total_rating: z.number(),
  round: RatingComponentSchema,
  month: RatingComponentSchema,
  bonus: RatingComponentSchema,
})

export type RatingComponent = z.infer<typeof RatingComponentSchema>
export type BreakdownData = z.infer<typeof BreakdownSchema>

// Player breakdown API
async function getPlayerBreakdown({
  competition_id,
  player_id,
}: {
  competition_id: number
  player_id: number
}) {
  const response = await axios.get(
    `/api/competitions/${competition_id}/ranks/players/${player_id}/breakdown`
  )
  return BreakdownSchema.parse(response.data)
}

export function usePlayerBreakdown({
  competition_id,
  player_id,
  enabled,
}: {
  competition_id: number
  player_id?: number
  enabled: boolean
}) {
  return useQuery({
    queryKey: ['playerBreakdown', { competition_id, player_id }],
    queryFn: () =>
      getPlayerBreakdown({ competition_id, player_id: player_id! }),
    enabled: enabled && player_id !== undefined,
  })
}

// Team breakdown API
async function getTeamBreakdown({
  competition_id,
  team_id,
}: {
  competition_id: number
  team_id: number
}) {
  const response = await axios.get(
    `/api/competitions/${competition_id}/ranks/teams/${team_id}/breakdown`
  )
  return BreakdownSchema.parse(response.data)
}

export function useTeamBreakdown({
  competition_id,
  team_id,
  enabled,
}: {
  competition_id: number
  team_id?: number
  enabled: boolean
}) {
  return useQuery({
    queryKey: ['teamBreakdown', { competition_id, team_id }],
    queryFn: () => getTeamBreakdown({ competition_id, team_id: team_id! }),
    enabled: enabled && team_id !== undefined,
  })
}
